import React, { memo, type ReactNode, useCallback, useEffect, useRef, useState } from 'react';

import { PanelLayout, type PanelResizeEnd } from '@atlaskit/navigation-system/layout/panel-layout';

import { usePanelActionsInternal } from '../../panel-manager/panel-provider';
import { usePanelStateInternal } from '../../panel-manager/use-panel-state-internal';
import { PanelContext } from '../../panels-renderer/panel-context';
import { PanelRouter } from '../../panels-renderer/panel-router';

const StablePanelRouter = memo(PanelRouter);

export type LayoutWithPanelProps = {
	/**
	 * Controlled preferred width, in pixels. Subsequent prop changes update the layout.
	 * Accept resize requests through `onPanelWidthChange` to allow user resizing;
	 * without a callback, the requested width stays controlled by this prop.
	 * Available space and bounds may constrain the rendered width without changing this request.
	 * Takes precedence over `defaultPanelWidth`. Do not switch between controlled and uncontrolled.
	 */
	panelWidth?: number;
	/**
	 * Initial uncontrolled width, in pixels. Subsequent prop changes are ignored.
	 * A previously saved provider preference takes precedence over this default on remount.
	 * Without a preference, navigation supplies 365px, or 400px with chat layout enabled.
	 * Compact layouts start at 320px unless a product-supplied or previously saved width exists.
	 */
	defaultPanelWidth?: number;
	/**
	 * Called with the requested width after a pointer resize or each keyboard resize step.
	 * Programmatic width changes do not invoke this callback.
	 *
	 * Provide this with `panelWidth` to control the rendered width, including programmatic width
	 * changes. For persistence that only needs the completed width, prefer `onResizeEnd`.
	 */
	onPanelWidthChange?: (panelWidth: number) => void;
	/**
	 * Minimum panel width. Defaults to 320px.
	 */
	panelMinWidth?: number;
	/**
	 * Optional maximum panel width.
	 */
	panelMaxWidth?: number;
	/**
	 * Localized accessible name for the panel region. Defaults to "Panel".
	 */
	panelLabel?: string;
	/**
	 * Localized accessible name for the resize control. Defaults to "Resize panel".
	 */
	resizeLabel?: string;
	testId?: string;
	children: ReactNode;
	/**
	 * Called when a pointer or keyboard resize interaction is complete.
	 * Intended for product-owned analytics and persistence that should only run once per interaction.
	 *
	 * Receives navigation's initialWidth, finalWidth (pixels), and the mode of the resize.
	 */
	onResizeEnd?: (event: PanelResizeEnd) => void;
};

/**
 * Adapts Panel System state and routed content to Navigation System's local-panel layout.
 * Mount only one local-panel host per navigation Root, including closed LayoutWithPanel
 * instances and directly mounted PanelLayout instances. Reuse this host for different panels.
 * Navigation owns responsive placement and resizing regardless of the chat-layout gate.
 * Panel System remembers explicit preferences; navigation owns defaults and rendered geometry.
 */
export function LayoutWithPanel({
	children,
	panelWidth,
	defaultPanelWidth,
	panelLabel,
	resizeLabel,
	onPanelWidthChange,
	panelMinWidth,
	panelMaxWidth,
	testId,
	onResizeEnd,
}: LayoutWithPanelProps): JSX.Element {
	const manager = usePanelActionsInternal();
	const state = usePanelStateInternal();
	const hasPanelContent = Boolean(
		state.activePanel &&
		(('entryPointReference' in state.activePanel && state.activePanel.entryPointReference) ||
			('panelContent' in state.activePanel && state.activePanel.panelContent)),
	);
	const [initialPreference] = useState(state.panelWidth ?? defaultPanelWidth);
	const isControlled = panelWidth !== undefined;
	const handleWidthChange = useCallback(
		(width: number) => {
			if (!isControlled) {
				manager.setPanelWidth(width);
			}
			onPanelWidthChange?.(width);
		},
		[isControlled, manager, onPanelWidthChange],
	);
	const widthToSync = panelWidth ?? initialPreference;
	useEffect(() => {
		if (widthToSync !== undefined) {
			manager.setPanelWidth(widthToSync);
		}
	}, [manager, widthToSync]);
	const activePanelRef = useRef(state.activePanel);
	const managerRef = useRef(manager);
	activePanelRef.current = state.activePanel;
	managerRef.current = manager;
	useEffect(
		() => () => {
			if (activePanelRef.current) {
				managerRef.current.closePanel(activePanelRef.current.instanceId);
			}
		},
		[],
	);
	return (
		<PanelLayout
			isPanelOpen={hasPanelContent}
			panelKey={state.activePanel?.instanceId}
			panelWidth={panelWidth}
			defaultPanelWidth={initialPreference}
			panelLabel={panelLabel}
			resizeLabel={resizeLabel}
			panelMinWidth={panelMinWidth}
			panelMaxWidth={panelMaxWidth}
			onPanelWidthChange={handleWidthChange}
			onResizeEnd={onResizeEnd}
			testId={testId}
			panel={
				state.activePanel && hasPanelContent ? (
					<PanelContext.Provider key={state.activePanel.instanceId} value={state.activePanel}>
						<StablePanelRouter />
					</PanelContext.Provider>
				) : null
			}
		>
			{children}
		</PanelLayout>
	);
}
